"use client";

import { FormEvent, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { FileText, Loader2, Upload } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { createLumiApiClient } from "@/lib/lumi-api";
import { LEARNING_LANGUAGES } from "@/components/LanguageMap";

const LANGUAGE_OPTIONS = LEARNING_LANGUAGES;
const LEVELS: Array<[string, string]> = [
  ["1", "Newbie"],
  ["3", "Beginner"],
  ["5", "Intermediate"],
  ["7", "Upper Intermediate"],
  ["9", "Advanced"],
  ["12", "Fluent"],
];
const ACCEPT = ".pdf,.txt,.md,application/pdf,text/plain";

interface BookUploadProps {
  /** When embedded in a dialog, drop the standalone card chrome/title. */
  embedded?: boolean;
  /** Called after a successful import (e.g. to close the dialog). */
  onSuccess?: () => void;
}

export default function BookUpload({ embedded = false, onSuccess }: BookUploadProps) {
  const router = useRouter();
  const api = useMemo(() => createLumiApiClient(), []);
  const fileInput = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<"file" | "text">("file");
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);

  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [sourceLanguage, setSourceLanguage] = useState("en");
  const [targetLanguage, setTargetLanguage] = useState("zh-CN");
  const [level, setLevel] = useState("5");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  function chooseFile(next: File | null) {
    if (!next) return;
    setFile(next);
    setTitle((current) => current || next.name.replace(/\.[^.]+$/, ""));
    setError(false);
    setMessage(null);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError(false);
    setMessage(null);

    try {
      if (mode === "file") {
        if (!file) {
          throw new Error("Choose a PDF or .txt file first.");
        }
        const form = new FormData();
        form.set("file", file);
        form.set("title", title || file.name);
        form.set("source_language", sourceLanguage);
        form.set("target_language", targetLanguage);
        form.set("language_level", level);
        await api.uploadBook(form);
      } else {
        await api.importTextBook({
          title,
          source_language: sourceLanguage,
          target_language: targetLanguage,
          original_name: `${title || "inline"}.txt`,
          content: text,
        });
      }

      setFile(null);
      setText("");
      setTitle("");
      setMessage("Added! Translation is preparing — your book will be ready shortly.");
      router.refresh();
      onSuccess?.();
    } catch (err) {
      setError(true);
      setMessage(err instanceof Error ? err.message : "Import failed");
    } finally {
      setLoading(false);
    }
  }

  const modeToggle = (
    <div className="flex overflow-hidden rounded-md border border-input text-xs font-semibold">
      <button
        className={`px-3 py-1 ${mode === "file" ? "bg-app-text text-app-bg" : "bg-app-surface text-app-body"}`}
        onClick={() => setMode("file")}
        type="button"
      >
        Upload file
      </button>
      <button
        className={`px-3 py-1 ${mode === "text" ? "bg-app-text text-app-bg" : "bg-app-surface text-app-body"}`}
        onClick={() => setMode("text")}
        type="button"
      >
        Paste text
      </button>
    </div>
  );

  return (
    <form
      className={
        embedded
          ? "space-y-4"
          : "space-y-4 rounded-md border border-app-border bg-app-surface p-5 shadow-sm"
      }
      onSubmit={submit}
    >
      {embedded ? (
        <div className="flex justify-end">{modeToggle}</div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-app-text">
            <Upload className="h-5 w-5" />
            <h3 className="text-base font-semibold">Add a book</h3>
          </div>
          {modeToggle}
        </div>
      )}

      {mode === "file" ? (
        <div
          className={`flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed px-4 py-8 text-center transition-colors ${
            dragging ? "border-app-teal bg-app-tinted" : "border-app-border bg-app-tinted"
          }`}
          onClick={() => fileInput.current?.click()}
          onDragLeave={() => setDragging(false)}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            chooseFile(e.dataTransfer.files?.[0] ?? null);
          }}
        >
          <FileText className="mb-2 h-7 w-7 text-app-body" />
          {file ? (
            <p className="text-sm font-medium text-app-text">{file.name}</p>
          ) : (
            <>
              <p className="text-sm font-medium text-app-text">
                Drop a PDF or .txt here, or click to choose
              </p>
              <p className="mt-1 text-xs text-gray-500">PDF and plain text supported</p>
            </>
          )}
          <input
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => chooseFile(e.target.files?.[0] ?? null)}
            ref={fileInput}
            type="file"
          />
        </div>
      ) : (
        <textarea
          className="min-h-32 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste the text you want to read…"
          required={mode === "text"}
          value={text}
        />
      )}

      <label className="block text-sm font-medium text-app-text">
        Title
        <Input
          className="mt-1"
          onChange={(e) => setTitle(e.target.value)}
          placeholder="The Fables of Aesop"
          value={title}
        />
      </label>

      <div className="grid grid-cols-3 gap-3">
        <label className="block text-sm font-medium text-app-text">
          From
          <select
            className="mt-1 h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
            onChange={(e) => setSourceLanguage(e.target.value)}
            value={sourceLanguage}
          >
            {LANGUAGE_OPTIONS.map(([code, label]) => (
              <option key={`s-${code}`} value={code}>{label}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm font-medium text-app-text">
          To
          <select
            className="mt-1 h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
            onChange={(e) => setTargetLanguage(e.target.value)}
            value={targetLanguage}
          >
            {LANGUAGE_OPTIONS.map(([code, label]) => (
              <option key={`t-${code}`} value={code}>{label}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm font-medium text-app-text">
          Level
          <select
            className="mt-1 h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
            onChange={(e) => setLevel(e.target.value)}
            value={level}
          >
            {LEVELS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
      </div>

      <Button className="w-full" disabled={loading} type="submit">
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Adding…
          </>
        ) : (
          "Add to library"
        )}
      </Button>

      {message && (
        <p className={`text-sm ${error ? "text-destructive" : "text-app-teal"}`}>{message}</p>
      )}
    </form>
  );
}
